import { useTranslation } from 'react-i18next';
import { useGetPricePlanBo } from '@utils/api/back-office/price-plans-bo/use-get-price-plan-bo';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { formatCurrency } from '@utils/helpers/currency';

interface PricePlanOverviewProps {
  /** Priceplan id; nothing renders when null/undefined. */
  pricePlanId?: number | null;
  /** Optional title override (e.g. "STT pricing tiers"). */
  title?: string;
  /** Unit suffix shown after each tier ("min", "char", ""). */
  unitLabel?: string;
}

/**
 * Compact read-only summary of a priceplan's tiers. Renders below an
 * existing-priceplan selector so the operator sees what they just picked
 * (from–to ranges + price per unit) without leaving the page.
 */
export default function PricePlanOverview({
  pricePlanId,
  title,
  unitLabel
}: PricePlanOverviewProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading } = useGetPricePlanBo(pricePlanId ?? 0);

  if (!pricePlanId) return null;
  if (isLoading || !data?.result) {
    return (
      <p className="text-xs" style={{ color: getColour('inactiveText') }}>
        {t('loading')}…
      </p>
    );
  }

  const plan = data.result;
  const tiers = plan.tiers ?? [];

  return (
    <div
      className="mt-2 p-3 rounded-2xl border text-xs"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('secondLayer')
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {title ?? plan.name}
        </span>
        <span
          className="text-[10px] uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {plan.type ?? 'STT'}
          {plan.unit_size != null && ` · ${plan.unit_size}/unit`}
        </span>
      </div>
      {tiers.length === 0 ? (
        <p style={{ color: getColour('inactiveText') }}>
          {t('no-tiers-added')}
        </p>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr style={{ color: getColour('inactiveText') }}>
              <th className="font-medium pb-1">{t('from')}</th>
              <th className="font-medium pb-1">{t('to')}</th>
              <th className="font-medium pb-1 text-right">
                {t('price-per-unit')}
              </th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, i) => (
              <tr key={i} style={{ color: getColour('normalText') }}>
                <td className="py-0.5">{tier.start ?? 0}</td>
                <td className="py-0.5">
                  {tier.end == null ? t('unlimited') : tier.end}
                </td>
                <td className="py-0.5 text-right">
                  {formatCurrency(Number(tier.price_per_unit ?? 0))}
                  {unitLabel ? `/${unitLabel}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
